import {
  useState, Fragment, useEffect, useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import { Button, LinkStyled } from '@components/common';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Transition } from '@headlessui/react';
import { SidebarGroup } from '@components/features/filters/partials/SidebarGroup';
import { JobSearchDomainsForm } from '@components/features/filters/forms/JobSearchDomainsForm';
import { JobSearchSortByForm } from '@components/features/filters/forms/JobSearchSortByForm';
import { JobSearchCareerLevelsForm } from '@components/features/filters/forms/JobSearchCareerLevelsForm';
import { JobSearchEmploymentTypesForm } from '@components/features/filters/forms/JobSearchEmploymentTypesForm';
import { JobSearchRemoteForm } from '@components/features/filters/forms/JobSearchRemoteForm';
import { JobSearchSpokenLanguagesForm } from '@components/features/filters/forms/JobSearchSpokenLanguagesForm';
import { mergeFilterValues } from '@components/features/filters/helpers/functions';
import { useFiltersHub } from '@hooks/filters/useFiltersHub';
import { JobSearchFiltersHubProps } from '@type/filters';
import { SidebarHeader } from '@components/features/filters/partials/SidebarHeader';
import { SidebarFooter } from '@components/features/filters/partials/SidebarFooter';
import { SEARCH_FORM_FILTERS_OBJECT } from '@components/features/filters/helpers/searchFiltersObject';
import { LocationForm } from '@components/features/filters/forms';
import { useFilterDrawer } from '@components/contexts/FilterDrawerContext';

const navigationLinks: { path: string, label: string }[] = [
  {
    path: '/applied-jobs',
    label: 'header.menu.applied_jobs',
  },
  {
    path: '/favorite-jobs',
    label: 'header.menu.favorite_jobs',
  },
  {
    path: '/cv-evaluations',
    label: 'cv.evaluations.page.title',
  },
];

/**
 * @description SidebarJobSearchFiltersHub - The hub for the sidebar job search filters.
 * * **queryParams** - the query params for the filters.
 * * **setQueryParams** - the function to set the query params.
 * * **toggleDrawer** - the function to toggle the drawer.
 */
export const SidebarJobSearchFiltersHub = (props: JobSearchFiltersHubProps) => {
// Destructure props
  const {
    queryParams,
    setQueryParams,
    toggleDrawer,
    activeSection,
    onUserSubmit,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Show details for filter groups
  const [showDetails, setShowDetails] = useState(false);
  const [isFooterHidden, setIsFooterHidden] = useState(false);

  const { setActiveSection } = useFilterDrawer();

  const { toggleDrawer: toggleDrawerCtx } = useFilterDrawer();
  const safeToggleDrawer = toggleDrawer ?? toggleDrawerCtx ?? (() => {});

  // FILTERS HUB HOOK
  // ***********************************************
  const {
    formRef,
    handleStep,
    record,
    setFormValues,
    submitForm,
    hardResetForm,
    formStatus,
  } = useFiltersHub({ queryParams, setQueryParams, externalFieldsList: ['keyword'] });

  const { filterLabels } = formStatus;

  // Ensure details view opens when an activeSection is requested
  useEffect(() => {
    if (activeSection) setShowDetails(true);
  }, [activeSection]);

  // Wraps submitForm to mark the submit as user-initiated. Parent uses this to gate URL
  // updates so auto-submits caused by display-only injected values (decomposed filters)
  // don't rewrite the URL.
  const userSubmitForm = useCallback(() => {
    onUserSubmit?.();
    submitForm();
  }, [onUserSubmit, submitForm]);

  const handleSubmitAndClose = () => {
    userSubmitForm();
    setShowDetails(false);
    setActiveSection(null);
    safeToggleDrawer();
  };

  // Render component
  // ***********************************************
  return (
    <form
      noValidate
      // Apply button is type="submit" — real clicks dispatch a trusted submit event.
      // submitTrigger() dispatches a synthetic event (isTrusted=false), so this differentiates
      // the user pressing Apply from the hub's auto-submit-on-populate.
      onSubmit={(e) => {
        if (e.isTrusted) onUserSubmit?.();
        handleStep(e);
      }}
      ref={formRef}
      className="relative flex h-full flex-col bg-surface"
    >

      <div className="scrollbar relative grow overflow-auto">
        {/* MAIN HEADER */}
        <Transition
          as={Fragment}
          show={!showDetails}
          enter="transform transition ease-in-out duration-200"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transform transition ease-in-out duration-200"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
          unmount={false}
        >
          <SidebarHeader
            title={t('search.filters.title')}
            hasDeleteButton={formStatus?.form && Object.keys(formStatus.form).length > 0}
            deleteButtonLabel={t('filters.delete-all.label')}
            deleteButtonClick={() => {
              // Reset sidebar filters
              hardResetForm(formRef);
              setTimeout(() => userSubmitForm(), 50);
            }}
          />
        </Transition>

        <SidebarGroup
          title={t('header.menu.applied_jobs')}
          emptyLabel=""
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          arrowClassName="text-secondary"
          onDetailsToggle={(open) => {
            setIsFooterHidden(open);
          }}
        >
          <div className="flex flex-col gap-2">
            {navigationLinks.map((navLink) => (
              <div
                key={`navlink_${navLink.label}`}
                className="hover:bg-surface-50 hover:text-ink"
              >
                <LinkStyled
                  href={navLink.path}
                  styling="none"
                  rounding="none"
                  className="flex cursor-pointer items-center gap-1 px-5 py-1.5 text-ink-medium hover:text-ink"
                >
                  {t(navLink.label) }
                </LinkStyled>
              </div>
            ))}
          </div>
        </SidebarGroup>

        {/* Sort by group */}
        <SidebarGroup
          title={t(SEARCH_FORM_FILTERS_OBJECT.sortBy.label)}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          activeFilters={mergeFilterValues([filterLabels?.sort ?? [SEARCH_FORM_FILTERS_OBJECT.sortBy.fields[0].filtersLabel]], t)}
          clearGroup={() => setFormValues({ sort: '' })}
          submitFunc={userSubmitForm}
        >
          <JobSearchSortByForm recordFunc={record} submitFunc={handleSubmitAndClose} />
        </SidebarGroup>

        {/* Location group */}
        <div data-test-id="filters-group-location">
          <SidebarGroup
            title={t('global.location.label')}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
            activeFilters={mergeFilterValues([filterLabels?.location, filterLabels?.remote], t)}
            clearGroup={() => setFormValues({ location: '', remote: '' })}
            submitFunc={userSubmitForm}
            isActive={activeSection === 'location'}
            onDetailsToggle={(open) => {
              setIsFooterHidden(open);
              // Important: when the user goes back from details, reset activeSection so it won’t auto-reopen
              if (!open && activeSection === 'location') {
                setActiveSection(null);
              }
            }}
          >
            <LocationForm
              recordFunc={record}
              submitFunc={handleSubmitAndClose}
              withSuggestedLocations
              withUserLocation
              withRadiusInKm={false}
              isActive={activeSection === 'location'}
            />
            <JobSearchRemoteForm recordFunc={record} submitFunc={handleSubmitAndClose} />
          </SidebarGroup>
        </div>

        {/* Domains group */}
        <SidebarGroup
          title={t(SEARCH_FORM_FILTERS_OBJECT.domains.label)}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          activeFilters={mergeFilterValues([filterLabels?.domain], t)}
          clearGroup={() => setFormValues({ domain: '' })}
          submitFunc={userSubmitForm}
        >
          <JobSearchDomainsForm recordFunc={record} submitFunc={handleSubmitAndClose} />
        </SidebarGroup>

        {/* Career Levels group */}
        <SidebarGroup
          title={t(SEARCH_FORM_FILTERS_OBJECT.careerLevels.label)}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          activeFilters={mergeFilterValues([filterLabels?.careerLevels], t)}
          clearGroup={() => setFormValues({ careerLevels: '' })}
          submitFunc={userSubmitForm}
        >
          <JobSearchCareerLevelsForm recordFunc={record} submitFunc={handleSubmitAndClose} />
        </SidebarGroup>

        {/* Employment Types group */}
        <SidebarGroup
          title={t(SEARCH_FORM_FILTERS_OBJECT.employmentTypes.label)}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          activeFilters={mergeFilterValues([filterLabels?.employmentTypes], t)}
          clearGroup={() => setFormValues({ employmentTypes: '' })}
          submitFunc={userSubmitForm}
        >
          <JobSearchEmploymentTypesForm recordFunc={record} submitFunc={handleSubmitAndClose} />
        </SidebarGroup>

        {/* Spoken Languages group */}
        <SidebarGroup
          title={t(SEARCH_FORM_FILTERS_OBJECT.spokenLanguages.label)}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          activeFilters={mergeFilterValues([filterLabels?.spokenLanguages], t)}
          clearGroup={() => setFormValues({ spokenLanguages: '' })}
          submitFunc={userSubmitForm}
        >
          <JobSearchSpokenLanguagesForm recordFunc={record} submitFunc={handleSubmitAndClose} />
        </SidebarGroup>
      </div>

      {/* Sidebar footer */}
      {!isFooterHidden && (
        <SidebarFooter showDetails={showDetails} setShowDetails={setShowDetails} toggleDrawer={safeToggleDrawer} />
      )}

      {/* Close button */}
      <Button
        type="button"
        styling="none"
        className="absolute -left-11 top-3.5 flex size-9 items-center justify-center rounded-full bg-surface-100 text-ink shadow-md hover:bg-surface-200 hover:shadow-lg md:-left-13"
        onClick={safeToggleDrawer}
      >
        <XMarkIcon className="size-5" />
      </Button>
    </form>
  );
};
